import { Temporal } from "@js-temporal/polyfill";
/**
 * Extract `YYYY-MM-DD` from a Date using its **local** calendar fields.
 *
 * Use for dates that originate from calendar / date-picker widgets where
 * the local year/month/day represent the user's visual selection — not an
 * instant-in-time that should be converted through a target timezone.
 *
 * For "what day is it right now in Berlin?" use {@link dateToBerlinIsoDay} instead.
 */
export function dateToLocalIsoDay(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
/**
 * Add (or subtract) calendar days from a `YYYY-MM-DD` string.
 */
export function addDaysToIsoDay(day, deltaDays) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    const pd = Temporal.PlainDate.from(day);
    return pd.add({ days: deltaDays }).toString();
}
/**
 * Add a calendar duration to a `YYYY-MM-DD` string, returning the resulting day.
 * Calendar-correct: a month roll-over clamps to the shorter month (e.g. Jan 31 + 1 month
 * → Feb 28/29) and a week is a true 7-day span; throws on an invalid day.
 */
export function addDurationToIsoDay(day, duration) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    return Temporal.PlainDate.from(day).add(duration).toString();
}
/**
 * Format a `YYYY-MM-DD` day as de-DE `"DD.MM.YYYY"`
 * (e.g. `"2025-12-31"` → `"31.12.2025"`); throws on an invalid day.
 *
 * Timezone-free: a calendar date is reordered as-is, never round-tripped through
 * an instant — so it is stable regardless of the runtime's timezone. Use this for
 * date-only values; for an instant-in-time use {@link formatBerlinDate}.
 */
export function formatIsoDayDe(day) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    const [year, month, dayOfMonth] = day.split("-");
    return `${dayOfMonth}.${month}.${year}`;
}
/**
 * Validate that a string is a real ISO calendar date
 * (e.g. rejects `"2024-02-30"` but accepts `"2024-02-29"`).
 */
export function isValidIsoDay(day) {
    try {
        return Temporal.PlainDate.from(day).toString() === day;
    }
    catch {
        return false;
    }
}
/**
 * Convert a `YYYY-MM-DD` string to a Date at **local** noon.
 *
 * Use for initializing calendar / date-picker widgets where the Date's
 * local calendar fields must match the given day string, regardless of
 * the browser's timezone.  Noon avoids midnight-rollover ambiguity.
 */
export function isoDayToLocalNoon(day) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    const parts = day.split("-").map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
}
/**
 * Generate all `YYYY-MM-DD` strings in an inclusive range.
 * Both boundaries must be `YYYY-MM-DD` strings.
 */
export function getIsoDaysInRange(from, to) {
    if (!isValidIsoDay(from)) {
        throw new Error(`Expected YYYY-MM-DD day for 'from', got: "${from}"`);
    }
    if (!isValidIsoDay(to)) {
        throw new Error(`Expected YYYY-MM-DD day for 'to', got: "${to}"`);
    }
    if (from > to) {
        throw new Error("getIsoDaysInRange: 'from' cannot be after 'to'");
    }
    const days = [];
    let current = Temporal.PlainDate.from(from);
    const end = Temporal.PlainDate.from(to);
    const MAX_DAYS = 366;
    while (Temporal.PlainDate.compare(current, end) <= 0) {
        if (days.length >= MAX_DAYS) {
            throw new Error(`getIsoDaysInRange: Maximum iteration limit (${MAX_DAYS}) exceeded`);
        }
        days.push(current.toString());
        current = current.add({ days: 1 });
    }
    return days;
}
/**
 * Add `n` business days (Monday–Friday) to a `YYYY-MM-DD` string.
 * Weekends are skipped; public holidays are not excluded.
 * Note: `n = 0` returns the input unchanged and does not normalize to a
 * weekday, so a weekend input stays on the weekend.
 */
export function addBusinessDaysToIsoDay(day, n) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    if (!Number.isInteger(n) || n < 0) {
        throw new Error(`Expected non-negative integer for n, got: ${n}`);
    }
    let pd = Temporal.PlainDate.from(day);
    let remaining = n;
    while (remaining > 0) {
        pd = pd.add({ days: 1 });
        if (pd.dayOfWeek <= 5)
            remaining--;
    }
    return pd.toString();
}
